import { Middleware } from 'redux';
import axios from 'axios';
import { NOTIFICATION_SHOW } from './notification.actions';
import { t } from 'i18next';
import * as Sentry from '@sentry/react';

export const errorMiddleware: Middleware = store => next => action => {
  const result = next(action);
  if (!result || typeof result.then !== 'function') {
    return result;
  }

  // Handle thunk promise result
  return result.catch((error: unknown) => {
    const { dispatch } = store;

    // Cancelled requests don't need any handling
    if (axios.isCancel(error)) {
      return;
    }

    // Non-axios errors (e.g. TypeError from JSON.parse) — report to Sentry as-is
    if (!axios.isAxiosError(error)) {
      Sentry.captureException(error);
      dispatch({
        type: NOTIFICATION_SHOW,
        payload: { message: t('something_went_wrong'), severity: 'error' },
      });
      return;
    }

    // Network errors (timeout, no connection, DNS failure, etc.)
    // These are not application bugs — show a user-friendly message without reporting to Sentry
    if (!error.response) {
      dispatch({
        type: NOTIFICATION_SHOW,
        payload: { message: t('network_error'), severity: 'warning' },
      });
      return;
    }

    const status = error.response.status;
    // Handle different response error types
    // 401 doesn't need to be handled, we simply logout
    if (status === 401) {
      return;
    }
    if (status >= 500) {
      dispatch({
        type: NOTIFICATION_SHOW,
        payload: { message: 'Server error occurred', severity: 'error' },
      });
      return;
    }
    if (status === 404) {
      dispatch({
        type: NOTIFICATION_SHOW,
        payload: { message: 'Resource not found', severity: 'warning' },
      });
      return;
    }
    if (status === 403) {
      dispatch({
        type: NOTIFICATION_SHOW,
        payload: { message: 'Access denied', severity: 'warning' },
      });
      return;
    }

    Sentry.withScope(scope => {
      scope.setTag('layer', 'redux-middleware');
      scope.setTag('middleware', 'errorMiddleware');
      if (action?.type) scope.setTag('redux.action_type', String(action.type));
      if (typeof status !== 'undefined')
        scope.setTag('http.status_code', String(status));

      const method = error?.config?.method;
      const url = error?.config?.url;
      if (method || url) {
        scope.setContext('http', {
          method: method ? String(method).toUpperCase() : undefined,
          url: url ? String(url) : undefined,
        });
      }

      // Keep extras minimal to avoid leaking sensitive data
      scope.setExtra('redux.action', { type: action?.type });
      scope.setExtra('http.params', error?.config?.params);

      Sentry.captureException(error);
    });

    dispatch({
      type: NOTIFICATION_SHOW,
      payload: {
        message: t('something_went_wrong'),
        severity: 'error',
      },
    });
  });
};
